import { libraryViewToSearch, parseLibraryView, readLibraryView, type LibraryView } from '@/services/library/library-view'

import { getAssistantSurface } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'

const LIBRARY_PATH = '/library'

/** The Library list loads up to six lists; past this the filters are applied but no count is claimed. */
const COUNT_WAIT_MS = 4_000
const POLL_MS = 100

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

const sameView = (a: LibraryView, b: LibraryView) =>
  a.search === b.search && a.type === b.type && a.sharing === b.sharing && a.sort === b.sort && a.dir === b.dir

/** The count the Library page reports for exactly this view, once it has loaded. */
async function awaitLibraryCount(view: LibraryView): Promise<number | null> {
  const deadline = Date.now() + COUNT_WAIT_MS

  while (Date.now() < deadline) {
    const handle = getAssistantSurface()

    if (handle?.kind === 'library') {
      const lib = handle.readContext().library

      if (lib.view === 'list' && lib.filters && !lib.loading && sameView(lib.filters, view)) {
        return lib.visibleCount ?? 0
      }
    }

    await delay(POLL_MS)
  }

  return null
}

/**
 * `refusal` is the route manifest's answer for `/library` for this caller,
 * resolved by `executeClientTool` (which owns the resolver), or null when allowed.
 */
export async function executeShowLibrary(
  input: unknown,
  deps: ExecuteClientToolDeps,
  refusal: string | null,
): Promise<unknown> {
  const view = readLibraryView(input)
  if ('error' in view) return view

  if (refusal) return { refused: refusal }

  const discard = (input as { discardUnsaved?: unknown } | null)?.discardUnsaved === true

  if (deps.currentRoute() !== LIBRARY_PATH && !discard) {
    const unsaved = deps.hasUnsavedWork()
    if (unsaved) return { blocked_unsaved_work: unsaved }
  }

  const target = `${LIBRARY_PATH}${libraryViewToSearch(view)}`
  const onLibrary = deps.currentRoute() === LIBRARY_PATH
  const currentView = parseLibraryView(new URLSearchParams(deps.currentSearch?.() ?? ''))

  // Showing what is already shown must not add a Back entry; compare parsed views so query order is irrelevant.
  if (onLibrary && sameView(currentView, view)) deps.navigate(target, { replace: true })
  else deps.navigate(target)

  const visibleCount = await awaitLibraryCount(view)

  return visibleCount === null
    ? { shown: view, visibleCount: null, note: 'The Library did not report a count in time; the filters are applied.' }
    : { shown: view, visibleCount }
}
